import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PageTransition from "@/components/PageTransition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamJoin() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState("joining"); // joining | success | error
  const [message, setMessage] = useState("");
  const [teamId, setTeamId] = useState("");

  useEffect(() => {
    joinTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- join once per inviteCode; joinTeam intentionally excluded
  }, [inviteCode]);

  const joinTeam = async () => {
    try {
      const response = await axios.post(`${API}/teams/join/${inviteCode}`);
      setStatus("success");
      setMessage(response.data.message);
      setTeamId(response.data.team_id);
      toast.success(response.data.message);
    } catch (error) {
      setStatus("error");
      setMessage(error.response?.data?.detail || "Failed to join team");
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] w-96" data-testid="team-join-card">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              {status === "joining" && (
                <>
                  <div className="w-12 h-12 border-4 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-[var(--qn-foreground-muted)]">Joining team...</p>
                </>
              )}
              {status === "success" && (
                <>
                  <CheckCircle className="w-12 h-12 text-[var(--qn-success)] mx-auto" />
                  <h3 className="text-xl font-outfit font-semibold">{message}</h3>
                  <Button onClick={() => navigate("/dashboard")} className="btn-primary" data-testid="go-dashboard-btn">
                    <Users className="w-4 h-4 mr-2" />
                    Go to Dashboard
                  </Button>
                </>
              )}
              {status === "error" && (
                <>
                  <XCircle className="w-12 h-12 text-red-400 mx-auto" />
                  <h3 className="text-xl font-outfit font-semibold">Unable to Join</h3>
                  <p className="text-[var(--qn-foreground-muted)] text-sm">{message}</p>
                  <Button onClick={() => navigate("/dashboard")} variant="outline" className="border-[var(--qn-border)]">
                    Go to Dashboard
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </PageTransition>
  );
}
